import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Share2, Trash2, Clock, Users, ShoppingBag, Bell, Crown, ExternalLink, Copy, CheckCircle, X } from 'lucide-react';
import BottomNavigation from '../components/BottomNavigation';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const MyWishlistsPage = () => {
  const navigate = useNavigate();
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [selectedWishlist, setSelectedWishlist] = useState(null);
  const [newWishlistName, setNewWishlistName] = useState('');
  const [newWishlistDesc, setNewWishlistDesc] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchWishlists();
    fetchPendingApprovals();
    fetchSubscriptionStatus();
  }, []);

  const fetchWishlists = async () => {
    try {
      const response = await axios.get(`${API}/wishlists`, { withCredentials: true });
      setWishlists(response.data.wishlists || []);
    } catch (error) {
      console.error('Failed to fetch wishlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get(`${API}/wishlists/pending-approvals`, { withCredentials: true });
      setPendingApprovals(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/wishlists/subscription-status`, { withCredentials: true });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const handleCreateWishlist = async () => {
    if (!newWishlistName.trim()) {
      toast.error('Please enter a wishlist name');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/wishlists`, {
        name: newWishlistName,
        description: newWishlistDesc
      }, { withCredentials: true });
      
      toast.success(response.data.message || 'Wishlist created!');
      setShowCreateModal(false);
      setNewWishlistName('');
      setNewWishlistDesc('');
      fetchWishlists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create wishlist');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteWishlist = async (wishlistId) => {
    if (!window.confirm('Delete this wishlist and all its items?')) return;
    
    try {
      await axios.delete(`${API}/wishlists/${wishlistId}`, { withCredentials: true });
      toast.success('Wishlist deleted');
      fetchWishlists();
    } catch (error) {
      toast.error('Failed to delete wishlist');
    }
  };

  const handleEnableSharing = async (wishlist) => {
    try {
      const response = await axios.post(`${API}/wishlists/${wishlist.id}/enable-sharing`, {}, { withCredentials: true });
      setSelectedWishlist({
        ...wishlist,
        share_code: response.data.share_code,
        share_link: response.data.share_link
      });
      setShowShareModal(true);
      fetchWishlists();
    } catch (error) {
      toast.error('Failed to enable sharing');
    }
  };

  const handleApproveAccess = async (wishlistId, requestId, action) => {
    try {
      await axios.post(`${API}/wishlists/${wishlistId}/approve-access/${requestId}`, { action }, { withCredentials: true });
      toast.success(action === 'approve' ? 'Access granted!' : 'Request rejected');
      fetchPendingApprovals();
    } catch (error) {
      toast.error('Failed to process request');
    }
  };

  const handleRevokeAccess = async (wishlistId, requesterEmail, requesterName) => {
    if (!window.confirm(`Revoke access for ${requesterName}?`)) return;
    
    try {
      await axios.post(`${API}/wishlists/${wishlistId}/revoke-access`, 
        { requester_email: requesterEmail }, 
        { withCredentials: true }
      );
      toast.success('Access revoked');
      fetchWishlists();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getExpiryStatus = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) return { text: 'Expired', color: 'text-red-500' };
    if (daysLeft <= 7) return { text: `${daysLeft}d left`, color: 'text-orange-500' };
    return { text: `${daysLeft}d left`, color: 'text-green-500' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4 shadow-xl">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/wishlist')} className="text-white">
            <ArrowLeft size={22} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">My Wishlists</h1>
            <p className="text-xs text-gray-200">{wishlists.length}/3 wishlists created</p>
          </div>
          {wishlists.length < 3 && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-white text-contribo-navy px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1"
            >
              <Plus size={16} /> New
            </button>
          )}
        </div>
      </div>

      {/* Pending Approvals */}
      {pendingApprovals.length > 0 && (
        <div className="mx-4 mt-4 bg-orange-500/20 border border-orange-500/50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="text-orange-400" size={18} />
            <span className="text-sm font-semibold text-orange-400">Pending Access Requests ({pendingApprovals.length})</span>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {pendingApprovals.map(req => (
              <div key={req.id} className="bg-white/10 rounded-lg p-2 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{req.requester_name}</p>
                  <p className="text-gray-400 text-xs truncate">wants access to {req.wishlist_name}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleApproveAccess(req.wishlist_id, req.id, 'approve')}
                    className="bg-green-500 text-white px-2 py-1 rounded text-xs"
                  >Approve</button>
                  <button
                    onClick={() => handleApproveAccess(req.wishlist_id, req.id, 'reject')}
                    className="bg-red-500 text-white px-2 py-1 rounded text-xs"
                  >Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription Banner */}
      {!subscriptionStatus?.is_subscribed && (
        <div className="mx-4 mt-4 bg-gradient-to-r from-purple-600/30 to-contribo-gold/30 border border-purple-500/50 rounded-xl p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="text-contribo-gold" size={20} />
              <div>
                <p className="text-white text-sm font-semibold">Extend Retention</p>
                <p className="text-gray-300 text-xs">Keep wishlists for 1 year @ ₹49</p>
              </div>
            </div>
            <button
              onClick={() => setShowSubscribeModal(true)}
              className="bg-contribo-gold text-contribo-navy px-3 py-1.5 rounded-lg text-xs font-bold"
            >Subscribe</button>
          </div>
        </div>
      )}

      {/* Wishlists */}
      <div className="p-4 space-y-3">
        {wishlists.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="mx-auto text-gray-500 mb-3" size={48} />
            <p className="text-white font-semibold">No wishlists yet</p>
            <p className="text-gray-400 text-sm mt-1">Create your first wishlist!</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 bg-contribo-gold text-contribo-navy px-4 py-2 rounded-lg font-semibold text-sm"
            >Create Wishlist</button>
          </div>
        ) : (
          wishlists.map(wishlist => {
            const expiry = getExpiryStatus(wishlist.expires_at);
            return (
              <div key={wishlist.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => navigate(`/wishlist/edit/${wishlist.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 truncate">{wishlist.name}</h3>
                      {wishlist.description && (
                        <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">{wishlist.description}</p>
                      )}
                    </div>
                    {wishlist.is_subscribed && (
                      <Crown className="text-contribo-gold flex-shrink-0" size={18} />
                    )}
                  </div>
                  
                  {/* Share Code Display */}
                  {wishlist.share_code && (
                    <div className="mt-2 bg-purple-50 border border-purple-200 rounded-lg px-2 py-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Share2 size={12} className="text-purple-600" />
                        <span className="text-xs text-purple-700 font-medium">Code:</span>
                        <span className="text-xs text-purple-900 font-bold">{wishlist.share_code}</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(wishlist.share_code); }}
                        className="text-purple-600 hover:text-purple-800 p-1"
                      >
                        <Copy size={12} />
                      </button>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className="flex items-center gap-1 text-gray-500">
                      <ShoppingBag size={14} /> {wishlist.item_count || 0} items
                    </span>
                    <span className={`flex items-center gap-1 ${expiry.color}`}>
                      <Clock size={14} /> {expiry.text}
                    </span>
                    {wishlist.pending_access_requests > 0 && (
                      <span className="flex items-center gap-1 text-orange-500">
                        <Users size={14} /> {wishlist.pending_access_requests} pending
                      </span>
                    )}
                  </div>
                  
                  {/* People with Access */}
                  {wishlist.access_granted_to && wishlist.access_granted_to.length > 0 && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-2">
                      <p className="text-xs font-semibold text-green-700 mb-1.5 flex items-center gap-1">
                        <Users size={12} /> People with Access ({wishlist.access_granted_to.length})
                      </p>
                      <div className="space-y-1.5 max-h-32 overflow-y-auto">
                        {wishlist.access_granted_to.map((person, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white rounded px-2 py-1.5 text-xs">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-800 truncate">{person.requester_name}</p>
                              <p className="text-gray-500 truncate text-[10px]">{person.requester_email}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                              <span className="text-green-600 text-[10px]">
                                {Math.ceil((new Date(person.access_expires_at) - new Date()) / (1000 * 60 * 60))}h
                              </span>
                              <button
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleRevokeAccess(wishlist.id, person.requester_email, person.requester_name); 
                                }}
                                className="text-red-500 hover:text-red-700 p-0.5"
                                title="Revoke access"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex border-t border-gray-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEnableSharing(wishlist); }}
                    className="flex-1 py-2.5 text-blue-600 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-blue-50"
                  >
                    <Share2 size={14} /> Share
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteWishlist(wishlist.id); }}
                    className="flex-1 py-2.5 text-red-500 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-red-50 border-l border-gray-100"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-5">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Create Wishlist</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Name *</label>
                <input
                  type="text"
                  value={newWishlistName}
                  onChange={(e) => setNewWishlistName(e.target.value)}
                  placeholder="e.g., Birthday Wishlist"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Description</label>
                <textarea
                  value={newWishlistDesc}
                  onChange={(e) => setNewWishlistDesc(e.target.value)}
                  placeholder="Optional description..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-600"
              >Cancel</button>
              <button
                onClick={handleCreateWishlist}
                disabled={creating}
                className="flex-1 py-2 bg-contribo-navy text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              >{creating ? 'Creating...' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedWishlist && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-5">
            <h2 className="text-lg font-bold text-gray-800 mb-2">Share Wishlist</h2>
            <p className="text-gray-500 text-xs mb-4">Share this code with friends to let them request access.</p>
            
            <div className="bg-gray-100 rounded-lg p-4 text-center mb-4">
              <p className="text-xs text-gray-500 mb-1">Share Code</p>
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm font-bold text-contribo-navy break-all">{selectedWishlist.share_code}</span>
                <button onClick={() => copyToClipboard(selectedWishlist.share_code)} className="text-blue-500 flex-shrink-0">
                  <Copy size={18} />
                </button>
              </div>
            </div>

            <div className="bg-gray-100 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">Share Link</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={selectedWishlist.share_link}
                  readOnly
                  className="flex-1 bg-transparent text-xs text-gray-700 truncate"
                />
                <button onClick={() => copyToClipboard(selectedWishlist.share_link)} className="text-blue-500">
                  <Copy size={16} />
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center mb-4">
              <CheckCircle className="inline text-green-500 mr-1" size={12} />
              Friends get access for 96 hours once you approve
            </p>

            <button
              onClick={() => setShowShareModal(false)}
              className="w-full py-2 bg-contribo-navy text-white rounded-lg text-sm font-semibold"
            >Done</button>
          </div>
        </div>
      )}

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <SubscribeModal
          onClose={() => setShowSubscribeModal(false)}
          onSuccess={() => { setShowSubscribeModal(false); fetchSubscriptionStatus(); }}
        />
      )}

      <BottomNavigation />
    </div>
  );
};

// Subscription Modal Component
const SubscribeModal = ({ onClose, onSuccess }) => {
  const [transactionId, setTransactionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [adminUpi, setAdminUpi] = useState('anushaammu1103@okicici');

  useEffect(() => {
    // Fetch admin UPI
    const fetchAdminUpi = async () => {
      try {
        const response = await axios.get(`${API}/admin/payment-settings/public`, { withCredentials: true });
        if (response.data.upi_id) {
          setAdminUpi(response.data.upi_id);
        }
      } catch (error) {
        console.error('Failed to fetch admin UPI:', error);
      }
    };
    fetchAdminUpi();
  }, []);

  const handleSubmit = async () => {
    if (!transactionId.trim()) {
      toast.error('Please enter transaction ID');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/wishlists/subscribe`, { transaction_id: transactionId }, { withCredentials: true });
      toast.success('Subscription submitted! Verification in progress.');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const copyUpi = () => {
    navigator.clipboard.writeText(adminUpi);
    toast.success('UPI ID copied!');
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-sm p-5 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-4">
          <Crown className="mx-auto text-contribo-gold mb-2" size={40} />
          <h2 className="text-lg font-bold text-gray-800">Extended Retention</h2>
          <p className="text-contribo-gold text-2xl font-bold mt-1">₹49<span className="text-sm text-gray-500">/year</span></p>
        </div>

        <div className="bg-purple-50 rounded-lg p-3 mb-4">
          <p className="text-xs text-purple-700">
            <strong>Benefits:</strong> Keep your wishlists for 1 full year instead of 30 days.
          </p>
          <p className="text-xs text-gray-500 mt-1 italic">
            This minimal fee helps us maintain storage and cover operational expenses.
          </p>
        </div>

        <div className="bg-gray-100 rounded-lg p-3 mb-4">
          <p className="text-xs text-gray-500 mb-1">Pay to UPI ID:</p>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800">{adminUpi}</span>
            <button onClick={copyUpi} className="text-blue-500">
              <Copy size={16} />
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-600 block mb-1">Transaction ID *</label>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Enter UPI transaction ID"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-600"
          >Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2 bg-contribo-gold text-contribo-navy rounded-lg text-sm font-bold disabled:opacity-50"
          >{submitting ? 'Submitting...' : 'Submit'}</button>
        </div>
      </div>
    </div>
  );
};

export default MyWishlistsPage;
